// Residence data cache for å optimalisere data-henting
// Dette unngår at hver bruker fra samme bofellesskap henter samme data

interface ResidenceCacheData {
  id: number;
  name: string;
  hero_image: string | null;
  pinned_message: any | null;
  news: any[];
  documents: any[];
  lastUpdated: number;
}

interface ResidenceCache {
  [residenceId: number]: ResidenceCacheData;
}

// Cache duration: 5 minutter for residence data
const CACHE_DURATION = 5 * 60 * 1000;

// Global cache
let residenceCache: ResidenceCache = {};

// Hent cached data hvis den er gyldig
export function getCachedResidenceData(residenceId: number): ResidenceCacheData | null {
  const cached = residenceCache[residenceId];
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.lastUpdated > CACHE_DURATION) {
    // Cache er utløpt
    delete residenceCache[residenceId];
    return null;
  }
  
  // logging fjernet
  return cached;
}

// Lagre data i cache
export function setCachedResidenceData(residenceId: number, data: Partial<ResidenceCacheData>): void {
  const now = Date.now();
  
  residenceCache[residenceId] = {
    ...residenceCache[residenceId],
    ...data,
    id: residenceId,
    lastUpdated: now
  };
  
  // logging fjernet
}

// Hent cached news hvis tilgjengelig
export function getCachedNews(residenceId: number): any[] | null {
  const cached = getCachedResidenceData(residenceId);
  return cached?.news || null;
}

// Hent cached pinned message hvis tilgjengelig
export function getCachedPinnedMessage(residenceId: number): any | null {
  const cached = getCachedResidenceData(residenceId);
  return cached?.pinned_message || null;
}

// Hent cached documents hvis tilgjengelige
export function getCachedDocuments(residenceId: number): any[] | null {
  const cached = getCachedResidenceData(residenceId);
  return cached?.documents || null;
}

// Sjekk om data er cached og gyldig
export function isResidenceDataCached(residenceId: number): boolean {
  return getCachedResidenceData(residenceId) !== null;
}

// Tøm cache (for testing eller når data endres)
export function clearResidenceCache(residenceId?: number): void {
  if (residenceId) {
    delete residenceCache[residenceId];
    console.log(`🗑️ Tømte cache for residence ${residenceId}`);
  } else {
    residenceCache = {};
    console.log(`🗑️ Tømte all residence cache`);
  }
}

// Debug: vis cache status
export function getCacheStatus(): { [key: number]: { lastUpdated: string; age: number } } {
  const now = Date.now();
  const status: { [key: number]: { lastUpdated: string; age: number } } = {};
  
  Object.keys(residenceCache).forEach(key => {
    const residenceId = parseInt(key);
    const cached = residenceCache[residenceId];
    status[residenceId] = {
      lastUpdated: new Date(cached.lastUpdated).toISOString(),
      age: Math.round((now - cached.lastUpdated) / 1000)
    };
  });
  
  return status;
}
